import { polygonContainsPoint, type PolygonPoint } from "../lib/C1_polygonPlacement";
import type { WallSide } from "../types/C1_ModelTypes";

export type PlacementWall = {
  start: PolygonPoint;
  end: PolygonPoint;
  wallId?: string;
  visible: boolean;
  side: WallSide;
};
export type RoomPlacementGeometry = { polygon: PolygonPoint[]; walls: PlacementWall[] };

/** Offsets actual wall segments to their interior faces and joins adjacent faces. */
export function createRoomPlacementGeometry(walls: (PlacementWall & { inset: number })[]): RoomPlacementGeometry {
  const area = walls.reduce((sum, wall) => sum + wall.start.x * wall.end.y - wall.end.x * wall.start.y, 0);
  const winding = area >= 0 ? 1 : -1;
  const faces = walls.map(wall => {
    const dx = wall.end.x - wall.start.x, dy = wall.end.y - wall.start.y;
    const length = Math.hypot(dx, dy) || 1;
    const offset = { x: -dy / length * wall.inset * winding, y: dx / length * wall.inset * winding };
    return { ...wall, start: { x: wall.start.x + offset.x, y: wall.start.y + offset.y },
      end: { x: wall.end.x + offset.x, y: wall.end.y + offset.y } };
  });
  const corners = faces.map((face, index) => {
    const previous = faces[(index + faces.length - 1) % faces.length];
    const a = { x: previous.end.x - previous.start.x, y: previous.end.y - previous.start.y };
    const b = { x: face.end.x - face.start.x, y: face.end.y - face.start.y };
    const cross = a.x * b.y - a.y * b.x;
    // Parallel faces of different thicknesses meet through a step, not a shared corner.
    if (Math.abs(cross) < 1e-9) return undefined;
    const delta = { x: face.start.x - previous.start.x, y: face.start.y - previous.start.y };
    const t = (delta.x * b.y - delta.y * b.x) / cross;
    return { x: previous.start.x + t * a.x, y: previous.start.y + t * a.y };
  });
  const joinedFaces = faces.map((face, index) => ({
    ...face,
    start: corners[index] ?? face.start,
    end: corners[(index + 1) % faces.length] ?? face.end,
  }));
  const polygon = joinedFaces.flatMap((face, index) => {
    const next = joinedFaces[(index + 1) % joinedFaces.length];
    return Math.hypot(face.end.x - next.start.x, face.end.y - next.start.y) > 1e-9
      ? [face.start, face.end]
      : [face.start];
  });
  return { polygon, walls: joinedFaces };
}

export function projectPointToWall(point: PolygonPoint, wall: Pick<PlacementWall, "start" | "end">) {
  const dx = wall.end.x - wall.start.x, dy = wall.end.y - wall.start.y;
  const lengthSquared = dx * dx + dy * dy;
  const offset = lengthSquared > 0 ? Math.max(0, Math.min(1,
    ((point.x - wall.start.x) * dx + (point.y - wall.start.y) * dy) / lengthSquared)) : 0;
  const position = { x: wall.start.x + dx * offset, y: wall.start.y + dy * offset };
  return { position, offset, distance: Math.hypot(point.x - position.x, point.y - position.y) };
}

/** Keeps an anchor in the usable polygon, including concave recesses. */
export function clampToRoomPolygon(point: PolygonPoint, geometry: RoomPlacementGeometry): PolygonPoint {
  if (polygonContainsPoint(geometry.polygon, point)) return point;
  let nearest: ReturnType<typeof projectPointToWall> | undefined;
  for (const wall of geometry.walls) {
    const candidate = projectPointToWall(point, wall);
    if (!nearest || candidate.distance < nearest.distance) nearest = candidate;
  }
  return nearest?.position ?? point;
}

/** Measures distance to the polygon, rather than to its bounding rectangle. */
export function distanceToRoomPolygon(point: PolygonPoint, polygon: PolygonPoint[]): number {
  if (polygonContainsPoint(polygon, point)) return 0;
  return polygon.reduce((distance, start, index) => Math.min(distance,
    projectPointToWall(point, { start, end: polygon[(index + 1) % polygon.length] }).distance), Infinity);
}
